import React, { useContext, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useField, useForm } from 'react-final-form';
import { InputField, TextAreaField, FlexColumn, FlexMiddle } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useHelpDeskCategories from '@ubeya/shared/hooks/account/useHelpDeskCategories';
import useHelpDeskAdmins from '@ubeya/shared/hooks/account/useHelpDeskAdmins';
import Tooltip from '@ubeya/shared-web/components/Tooltip/Tooltip';
import { ReactComponent as PhotoIcon } from '@ubeya/shared-web/assets/photo.svg';
import { ReactComponent as FileIcon } from '@ubeya/shared-web/assets/file.svg';
import { ReactComponent as RelatedIcon } from '@ubeya/shared-web/assets/help-desk/related.svg';
import ComposerAttachmentPreview from '../ComposerAttachmentPreview';
import HelpDeskTicketMetadata from '../HelpDeskTicketMetadata';
import MetaChip, { chipsRowStyle } from '../shared/MetaChip';
import { formatShiftTimeRange, toAcceptAttribute } from '../../helpDeskUtils';
import { ALLOWED_IMAGE_EXTENSIONS, ALLOWED_VIDEO_EXTENSIONS, ALLOWED_OTHER_EXTENSIONS } from '../../helpDeskConstants';
import { TITLE_MAX } from './validators';

const IMAGE_ACCEPT = toAcceptAttribute(ALLOWED_IMAGE_EXTENSIONS);
const NON_IMAGE_ACCEPT = toAcceptAttribute([...ALLOWED_VIDEO_EXTENSIONS, ...ALLOWED_OTHER_EXTENSIONS]);

const Wrapper = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SummaryBar = styled(FlexMiddle)`
  padding: 8px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  flex-shrink: 0;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  ${chipsRowStyle}
`;

const ShiftSummary = styled(MetaChip)`
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.grey4};
    flex-shrink: 0;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Body = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 24px;
  flex: 1;
`;

const Heading = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const AttachIcons = styled(FlexMiddle)`
  gap: 16px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const AttachSection = styled(FlexColumn)`
  gap: 12px;
  margin-top: 8px;
`;

const FieldWithCount = ({ name, title, max, ...rest }) => {
  const {
    input: { value }
  } = useField(name);
  const length = (value ?? '').length;
  return <InputField name={name} title={`${title} (${length}/${max})`} metadata={{ maxNumberOfChar: max }} {...rest} />;
};

const ShiftSummaryItem = ({ shift }) => {
  const { dateFormat, timeFormat } = useContext(ConfigContext);

  if (!shift) return null;

  const time = formatShiftTimeRange(shift, timeFormat);
  const label = [
    shift.projectName,
    shift.positionName,
    moment.utc(shift.date).format(dateFormat || 'MMM DD, YYYY'),
    time
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip body={label}>
      <ShiftSummary>
        <RelatedIcon />
        <span>{label}</span>
      </ShiftSummary>
    </Tooltip>
  );
};

const SelectionSummary = ({ accumulated, onChange }) => {
  const { accountId } = useAccount();
  const { branchId, categoryId, assignedAdminId, priority, shiftDetails } = accumulated || {};

  const { categories } = useHelpDeskCategories({ accountId, branchId, onlyMyCategories: true });
  const { admins } = useHelpDeskAdmins({ accountId, branchId, categoryId });

  const { input: categoryInput } = useField('categoryId');
  const { input: assigneeInput } = useField('assignedAdminId');
  const { input: priorityInput } = useField('priority');

  const handleCategory = useCallback(
    (nextCategoryId) => {
      categoryInput.onChange(nextCategoryId);
      const nextCategory = (categories || []).find((category) => category.id === nextCategoryId);
      const nextAssigneeId = nextCategory?.autoAssignAdminId ?? null;
      assigneeInput.onChange(nextAssigneeId);
      onChange({ categoryId: nextCategoryId, assignedAdminId: nextAssigneeId });
    },
    [categoryInput, assigneeInput, onChange, categories]
  );
  const handleAssignee = useCallback(
    (adminId) => {
      assigneeInput.onChange(adminId);
      onChange({ assignedAdminId: adminId });
    },
    [assigneeInput, onChange]
  );
  const handlePriority = useCallback(
    (nextPriority) => {
      priorityInput.onChange(nextPriority);
      onChange({ priority: nextPriority });
    },
    [priorityInput, onChange]
  );

  return (
    <SummaryBar>
      <HelpDeskTicketMetadata
        ticket={{ categoryId, assignedAdminId, priority }}
        categories={categories || []}
        admins={admins || []}
        isEditable
        truncateAssignee
        onCategoryChange={handleCategory}
        onAssigneeChange={handleAssignee}
        onPriorityChange={handlePriority}>
        {shiftDetails && <ShiftSummaryItem shift={shiftDetails} />}
      </HelpDeskTicketMetadata>
    </SummaryBar>
  );
};

const Step3Describe = ({ accumulated, onChange, fileUpload }) => {
  const { t } = useContext(ConfigContext);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const { pendingFiles, uploadingCount, handleFileSelect, removeFile } = fileUpload;

  const form = useForm();
  useEffect(() => {
    form.change('attachmentsUploading', uploadingCount > 0);
  }, [form, uploadingCount]);

  return (
    <Wrapper>
      <SelectionSummary accumulated={accumulated} onChange={onChange} />
      <Body>
        <Heading>{t('help_desk_describe_request')}</Heading>
        <FieldWithCount name="title" title={t('help_desk_ticket_title')} max={TITLE_MAX} required />
        <TextAreaField name="description" title={t('description')} required rows={6} />
        <AttachSection>
          <AttachIcons>
            <IconButton
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploadingCount > 0}
              title={t('help_desk_add_attachment')}
              aria-label={t('help_desk_add_attachment')}>
              <PhotoIcon />
            </IconButton>
            <IconButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingCount > 0}
              title={t('help_desk_add_attachment')}
              aria-label={t('help_desk_add_attachment')}>
              <FileIcon />
            </IconButton>
            <HiddenInput ref={imageInputRef} type="file" accept={IMAGE_ACCEPT} multiple onChange={handleFileSelect} />
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept={NON_IMAGE_ACCEPT}
              multiple
              onChange={handleFileSelect}
            />
          </AttachIcons>
          <ComposerAttachmentPreview attachments={pendingFiles} onRemove={removeFile} />
        </AttachSection>
      </Body>
    </Wrapper>
  );
};

export default Step3Describe;
